import { create } from 'zustand';
import { useEffect } from 'react';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { handleApiError } from '@mygpt/app';
import { permissionApi, Department, User, Role } from '../services/permissionApi';

export interface OrganizationNode {
  id: number | string;
  name: string;
  level: number;
  count: number;
  expanded?: boolean;
  children?: OrganizationNode[];
  originalData?: Department;
}

export interface MemberData {
  id: number | string;
  uuid?: string; // 用户UUID
  name: string;
  email: string;
  avatar: string;
  department: string;
  departmentPath: string;
  departmentId?: string;
  permissionLevel: number;
  permissionText: string; // 保留用于兼容性，但将使用roleCode进行实时翻译
  roleCode: string; // 添加roleCode用于实时翻译
  status: string;
  enabled?: boolean;
  joinDate: string;
  updateAt: string;
  apiKeyBound: boolean;
  apiKeys: string[];
  sysRoleId?: number;
}

// Zustand store 类型定义
interface PermissionState {
  // 状态
  loading: boolean;
  organizationData: OrganizationNode[];
  membersData: MemberData[];
  unassignedUsers: User[];
  roles: Role[];
  departments: Department[];
  companyName: string;

  // 基础 setters
  setLoading: (loading: boolean) => void;
  setCompanyName: (name: string) => void;
  setOrganizationData: (data: OrganizationNode[]) => void;
  setMembersData: (data: MemberData[]) => void;
  setUnassignedUsers: (users: User[]) => void;
  setRoles: (roles: Role[]) => void;
  setDepartments: (departments: Department[]) => void;
}

// 创建 Zustand store
const usePermissionStore = create<PermissionState>((set) => ({
  // 初始状态
  loading: false,
  organizationData: [],
  membersData: [],
  unassignedUsers: [],
  roles: [],
  departments: [],
  companyName: '',

  // 基础 setters
  setLoading: (loading) => set({ loading }),
  setCompanyName: (name) => set({ companyName: name }),
  setOrganizationData: (data) => set({ organizationData: data }),
  setMembersData: (data) => set({ membersData: data }),
  setUnassignedUsers: (users) => set({ unassignedUsers: users }),
  setRoles: (roles) => set({ roles }),
  setDepartments: (departments) => set({ departments }),
}));

/**
 * 权限管理 Hook - 基于 Zustand 状态管理
 * 提供组织架构、成员管理、角色管理等功能
 */
export const usePermissionData = () => {
  const { t } = useTranslation();

  // 从 Zustand store 获取状态和 setters
  const {
    loading,
    organizationData,
    membersData,
    unassignedUsers,
    roles,
    departments,
    companyName,
    setLoading,
    setCompanyName,
    setOrganizationData,
    setMembersData,
    setUnassignedUsers,
    setRoles,
    setDepartments,
  } = usePermissionStore();

  // 转换部门数据为组织节点
  const convertToOrganizationNode = (dept: Department, level: number = 0): OrganizationNode => {
    // 优先使用allUserCount，如果没有则回退到activedUserCount
    const userCount = dept.allUserCount ?? dept.activedUserCount ?? dept.userCount ?? 0;
    const childrenNodes = dept.children?.map((child) => convertToOrganizationNode(child, level + 1)) || [];

    return {
      id: dept.id,
      name: dept.name,
      level,
      count: userCount, // 只显示当前部门自己的用户数
      expanded: level < 2, // 默认展开前两级
      children: childrenNodes,
      originalData: dept,
    };
  };

  // 转换用户数据为成员数据
  const convertToMemberData = (
    user: User,
    departments: Department[],
    rootNodeName?: string,
    _unassignedText: string = '未分配' // 参数保留以兼容现有调用，但不使用
  ): MemberData => {
    // 根据部门ID查找部门路径
    let departmentPath = '__UNASSIGNED__'; // 使用特殊标识符
    let departmentName = '__UNASSIGNED__'; // 使用特殊标识符

    if (user.departmentId) {
      const dept = findDepartmentById(departments, user.departmentId);
      if (dept) {
        departmentName = dept.name;
        departmentPath = getDepartmentPath(departments, dept.id, [], rootNodeName, '__UNASSIGNED__');
      }
    }

    // 根据角色代码确定权限等级
    let permissionLevel = 5; // 默认查看者
    const roleCode = user.roleCode || 'ROLE_VIEWER';

    if (roleCode) {
      switch (roleCode) {
        case 'ROLE_COMPANY_OWNER':
          permissionLevel = 1;
          break;
        case 'ROLE_ADMIN':
          permissionLevel = 2;
          break;
        case 'ROLE_ADMIN_DEPARTMENT':
          permissionLevel = 3;
          break;
        case 'ROLE_EDITOR':
          permissionLevel = 4;
          break;
        case 'ROLE_VIEWER':
          permissionLevel = 5;
          break;
        default:
          permissionLevel = 5;
      }
    }

    return {
      id: user.id,
      uuid: user.uuid, // 传递用户UUID
      name: user.name,
      email: user.email,
      avatar: user.name.charAt(0).toUpperCase(),
      department: departmentName,
      departmentPath,
      departmentId: user.departmentId,
      permissionLevel,
      permissionText: '', // 占位符，实际翻译在渲染层处理
      roleCode,
      status: '', // 占位符，实际翻译在渲染层处理
      enabled: user.enabled && user.userCompanyEnabled, // 原始状态数据用于实时翻译
      joinDate: user.createdAt ? new Date(user.createdAt).toLocaleDateString('zh-CN') : '',
      updateAt: user.updatedAt
        ? new Date(user.updatedAt).toLocaleString('zh-CN')
        : t('adminConsole.newConsole.member.neverLoggedIn'),
      apiKeyBound: false, // API密钥信息需要单独获取
      apiKeys: [],
      sysRoleId: user.sysRoleId,
    };
  };

  // 查找部门
  const findDepartmentById = (departments: Department[], id: string): Department | null => {
    for (const dept of departments) {
      if (dept.id === id) {
        return dept;
      }
      if (dept.children) {
        const found = findDepartmentById(dept.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  // 获取部门路径（包含根节点）
  const getDepartmentPath = (
    departments: Department[],
    targetId: string,
    path: string[] = [],
    rootNodeName?: string,
    unassignedText: string = '__UNASSIGNED__'
  ): string => {
    // 使用传入的根节点名称或默认的公司名称作为根节点
    const rootName = rootNodeName || companyName;
    const rootPath = path.length === 0 ? [rootName] : path;

    for (const dept of departments) {
      const currentPath = [...rootPath, dept.name];
      if (dept.id === targetId) {
        return currentPath.join(' > ');
      }
      if (dept.children) {
        const foundPath = getDepartmentPath(dept.children, targetId, currentPath);
        if (foundPath && foundPath !== unassignedText) return foundPath;
      }
    }
    return unassignedText;
  };

  // 加载组织架构（带公司名称参数）
  const loadOrganizationDataWithCompanyName = async (companyNameParam?: string, showLoading: boolean = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }

      // 获取公司ID（如果需要的话）
      let companyId = '';
      try {
        const companyInfo = await permissionApi.getCurrentCompanyInfo();
        companyId = companyInfo?.id || '';
      } catch (error) {
        console.warn('获取公司ID失败，使用默认值:', error);
      }

      const departments = await permissionApi.getOrganizationStructure();
      setDepartments(departments);

      // 转换部门数据，使用API返回的activedUserCount
      const childrenNodes = departments.map((dept) => convertToOrganizationNode(dept, 1));

      // 调试：打印部门数据结构
      console.log('原始部门数据:', departments);
      console.log('转换后的节点:', childrenNodes);

      // 根节点的用户数：优先使用allUserCount，如果没有则回退到activedUserCount
      const totalUsers = departments.reduce((sum, dept) => sum + (dept.allUserCount ?? dept.activedUserCount ?? 0), 0);

      // 添加一个根节点，使用传入的公司名称或当前状态中的公司名称
      const rootNode: OrganizationNode = {
        id: 'root',
        name: companyNameParam || companyName || t('adminConsole.newConsole.common.defaultCompanyName'),
        level: 0,
        count: totalUsers, // 显示所有一级部门的用户数之和
        expanded: true,
        children: childrenNodes,
        originalData: {
          id: companyId,
          name: '',
          code: '',
          description: '',
          companyId,
        } as Department, // 添加公司ID到原始数据中
      };

      console.log('最终组织架构数据:', rootNode);
      setOrganizationData([rootNode]);
    } catch (error: any) {
      console.error('加载组织架构失败:', error);
      const errorMessage = handleApiError(error, t, t('adminConsole.newConsole.common.loadOrgDataFailed'));
      notifications.show({
        title: t('adminConsole.newConsole.common.loadFailed'),
        message: errorMessage,
        color: 'red',
      });
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  // 加载组织架构（使用当前公司名称）
  const loadOrganizationData = async (showLoading: boolean = false) => {
    await loadOrganizationDataWithCompanyName(companyName, showLoading);
  };

  // 加载成员列表
  const loadMembersData = async (updateOrgTree: boolean = true) => {
    try {
      // 只在真正需要时显示loading
      if (updateOrgTree) {
        setLoading(true);
      }

      // 并行加载用户数据和组织架构（如果需要）
      const promises: Promise<any>[] = [permissionApi.getUserList()];

      if (updateOrgTree) {
        promises.push(permissionApi.getOrganizationStructure());
      }

      const results = await Promise.all(promises);
      const { users, isolatedUsers } = results[0];

      // 如果更新了组织架构，使用新数据
      let currentDepartments = departments;
      if (updateOrgTree && results[1]) {
        currentDepartments = results[1];
        setDepartments(currentDepartments);

        // 更新组织架构树
        const childrenNodes = currentDepartments.map((dept) => convertToOrganizationNode(dept, 1));
        const totalUsers = currentDepartments.reduce(
          (sum, dept) => sum + (dept.allUserCount ?? dept.activedUserCount ?? 0),
          0
        );

        const rootNode: OrganizationNode = {
          id: 'root',
          name: organizationData.length > 0 ? organizationData[0].name : companyName,
          level: 0,
          count: totalUsers,
          expanded: true,
          children: childrenNodes,
        };

        setOrganizationData([rootNode]);
      }

      // 处理成员数据
      const rootNodeName = organizationData.length > 0 ? organizationData[0].name : companyName;
      const members = users.map((user: User) =>
        convertToMemberData(user, currentDepartments, rootNodeName, t('adminConsole.newConsole.unassigned'))
      );
      setMembersData(members);

      // 更新未分配用户
      const unassigned = users.filter((user: User) => (isolatedUsers as string[]).includes(user.id));
      setUnassignedUsers(unassigned);
    } catch (error: any) {
      console.error('加载成员列表失败:', error);
      const errorMessage = handleApiError(error, t, t('adminConsole.newConsole.member.loadFailed'));
      notifications.show({
        title: t('adminConsole.newConsole.member.loadFailed'),
        message: errorMessage,
        color: 'red',
      });
    } finally {
      if (updateOrgTree) {
        setLoading(false);
      }
    }
  };

  // 加载未分配用户 - 已在 loadMembersData 中处理，保留方法以兼容现有调用
  const loadUnassignedUsers = async () => {
    // 数据已在 loadMembersData 中更新，无需单独请求
    console.log('未分配用户数据已在 loadMembersData 中更新');
  };

  // 加载可分配的角色列表（用于用户新增和编辑）
  const loadAssignableRoles = async () => {
    try {
      const assignableRoleList = await permissionApi.getAssignableRoles();
      return assignableRoleList;
    } catch (error: any) {
      console.error('加载可分配角色列表失败:', error);
      return [];
    }
  };

  const init = async () => {
    setLoading(true);
    console.log('init - 加载初始权限数据');

    try {
      // 并行加载所有初始数据，大幅减少等待时间
      const [companyInfoResult, rolesResult, departmentsResult, userListResult] = await Promise.all([
        // 获取公司信息
        permissionApi.getCurrentCompanyInfo().catch((error) => {
          console.error('获取公司信息失败:', error);
          return { id: '', name: t('adminConsole.newConsole.common.defaultCompanyName') };
        }),
        // 获取角色列表
        permissionApi.getRoles().catch((error) => {
          console.error('获取角色列表失败:', error);
          return [];
        }),
        // 获取组织架构
        permissionApi.getOrganizationStructure().catch((error) => {
          console.error('获取组织架构失败:', error);
          return [];
        }),
        // 获取用户列表
        permissionApi.getUserList().catch((error) => {
          console.error('获取用户列表失败:', error);
          return { users: [], departmentUsers: {}, isolatedUsers: [] };
        }),
      ]);

      // 设置公司信息
      const currentCompanyName = companyInfoResult?.name || 'Company';
      setCompanyName(currentCompanyName);

      // 设置角色数据
      setRoles(rolesResult);

      // 设置部门数据
      setDepartments(departmentsResult);

      // 处理用户数据
      const { users, isolatedUsers } = userListResult;
      const members = users.map((user) =>
        convertToMemberData(user, departmentsResult, currentCompanyName, t('adminConsole.newConsole.unassigned'))
      );
      setMembersData(members);

      // 处理未分配用户
      const unassigned = users.filter((user: User) => (isolatedUsers as string[]).includes(user.id));
      setUnassignedUsers(unassigned);

      // 构建组织架构树
      const childrenNodes = departmentsResult.map((dept) => convertToOrganizationNode(dept, 1));
      const totalUsers = departmentsResult.reduce(
        (sum, dept) => sum + (dept.allUserCount ?? dept.activedUserCount ?? 0),
        0
      );

      const rootNode: OrganizationNode = {
        id: 'root',
        name: currentCompanyName,
        level: 0,
        count: totalUsers,
        expanded: true,
        children: childrenNodes,
      };

      setOrganizationData([rootNode]);
    } catch (error: any) {
      console.error('初始化加载失败:', error);
      const errorMessage = handleApiError(error, t, t('adminConsole.newConsole.permissions.loadFailedMessage'));
      notifications.show({
        title: t('adminConsole.newConsole.permissions.loadFailed'),
        message: errorMessage,
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  // 创建用户
  const createUser = async (userData: {
    name: string;
    email: string;
    password: string;
    departmentId?: string;
    sysRoleId?: number;
  }) => {
    try {
      setLoading(true);
      await permissionApi.createUser(userData);
      notifications.show({
        message: t('adminConsole.tip.create_success'),
        color: 'green',
      });
      // 只更新成员数据，如果用户有部门才更新组织架构
      await loadMembersData(!!userData.departmentId);
      return true;
    } catch (error: any) {
      const errorMessage = handleApiError(error, t);
      notifications.show({
        title: t('adminConsole.tip.create_error'),
        message: errorMessage,
        color: 'red',
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // 更新用户
  const updateUser = async (userId: string, userData: any) => {
    try {
      setLoading(true);
      await permissionApi.updateUser({
        id: userId,
        ...userData,
      });
      //   notifications.show({
      //     title: '更新成功',
      //     message: '用户信息已更新',
      //     color: 'green',
      //   });
      // 只更新成员数据，不需要重新加载组织架构
      await loadMembersData(false);
      return true;
    } catch (error: any) {
      const errorMessage = handleApiError(error, t);
      notifications.show({
        title: t('adminConsole.tip.update_error'),
        message: errorMessage,
        color: 'red',
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // 同时更新用户部门和角色
  const updateUserDepartmentAndRole = async (userIds: string[], departmentId: string, sysRoleId: string) => {
    try {
      setLoading(true);
      await permissionApi.updateUserDepartmentAndRole({
        userIds,
        departmentId,
        sysRoleId,
      });
      // 重新加载成员数据和组织架构，因为部门分配可能影响组织架构中的用户数量
      await loadMembersData(true);
      return true;
    } catch (error: any) {
      const errorMessage = handleApiError(error, t);
      notifications.show({
        title: t('adminConsole.tip.update_error'),
        message: errorMessage,
        color: 'red',
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // 切换用户状态
  const toggleUserStatus = async (userId: string, enabled: boolean, departmentId?: string) => {
    try {
      setLoading(true);
      await permissionApi.toggleUserStatus(userId, enabled, departmentId);
      notifications.show({
        title: t('adminConsole.newConsole.member.status.toggleSuccess'),
        message: t('adminConsole.newConsole.member.status.toggleSuccessMessage'),
        color: 'green',
      });
      // 状态更新影响组织架构的用户计数
      await loadMembersData(true);
      return true;
    } catch (error: any) {
      const errorMessage = handleApiError(error, t, t('adminConsole.newConsole.member.status.toggleFailed'));
      notifications.show({
        title: t('adminConsole.newConsole.member.status.toggleFailed'),
        message: errorMessage,
        color: 'red',
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // 重置密码
  const resetPassword = async (userId: string) => {
    try {
      setLoading(true);
      const newPassword = permissionApi.generatePassword();
      const { data } = await permissionApi.resetUserPassword(userId, newPassword);
      if (!data.success) {
        throw data.error;
      }
      return newPassword;
    } catch (error: any) {
      const errorMessage = handleApiError(error, t, t('adminConsole.newConsole.member.password.resetFailedMessage'));
      notifications.show({
        title: t('adminConsole.newConsole.member.password.resetFailed'),
        message: errorMessage,
        color: 'red',
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  // 分配用户到部门
  const assignUserToDepartment = async (userId: string, departmentId: string) => {
    try {
      setLoading(true);
      await permissionApi.assignUserToDepartment(userId, departmentId);
      notifications.show({
        message: t('adminConsole.newConsole.member.assignSuccess'),
        color: 'green',
      });
      // 部门变更需要更新组织架构的用户计数
      await loadMembersData(true);
      return true;
    } catch (error: any) {
      const errorMessage = handleApiError(error, t, t('adminConsole.newConsole.member.assignFailed'));
      notifications.show({
        title: t('adminConsole.newConsole.member.assignFailed'),
        message: errorMessage,
        color: 'red',
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // 批量导入用户
  const batchImportUsers = async (users: any[]) => {
    try {
      setLoading(true);
      const results = await permissionApi.batchImportUsers(users);
      const successCount = results.filter((r: any) => r.success).length;
      const failCount = results.filter((r: any) => !r.success).length;

      notifications.show({
        title: t('adminConsole.newConsole.import.importSuccess'),
        message: t('adminConsole.newConsole.import.importSuccessMessage', {
          successCount,
          failCount,
        }),
        color: successCount > 0 ? 'green' : 'red',
      });

      if (successCount > 0) {
        await loadMembersData(true);
      }

      return results;
    } catch (error: any) {
      const errorMessage = handleApiError(error, t);
      notifications.show({
        title: t('adminConsole.newConsole.import.importFailed'),
        message: errorMessage,
        color: 'red',
      });
      return [];
    } finally {
      setLoading(false);
    }
  };

  // 创建部门
  const createDepartment = async (deptData: { name: string; code: string; description: string; parentId: string }) => {
    try {
      await permissionApi.createDepartment({
        name: deptData.name,
        code: deptData.code,
        description: deptData.description,
        parentId: deptData.parentId,
      });

      // 只重新加载组织架构，不显示加载状态
      await loadOrganizationData(false);
      return true;
    } catch (error: any) {
      const errorMessage = handleApiError(error, t);
      notifications.show({
        title: t('adminConsole.tip.create_error'),
        message: errorMessage,
        color: 'red',
      });
      return false;
    }
  };

  // 更新部门
  const updateDepartment = async (deptData: { id: string; name: string; code: string; description: string }) => {
    try {
      await permissionApi.updateDepartment(deptData);

      // 只重新加载组织架构，不显示加载状态
      await loadOrganizationData(false);
      return true;
    } catch (error: any) {
      const errorMessage = handleApiError(error, t);
      notifications.show({
        title: t('adminConsole.tip.update_error'),
        message: errorMessage,
        color: 'red',
      });
      return false;
    }
  };

  // 删除部门
  const deleteDepartment = async (deptId: string) => {
    try {
      setLoading(true);
      await permissionApi.deleteDepartment(deptId);

      notifications.show({
        message: t('adminConsole.tip.delete_success'),
        color: 'green',
      });

      // 重新加载组织架构
      await loadOrganizationData(false);
      return true;
    } catch (error: any) {
      const errorMessage = handleApiError(error, t);
      notifications.show({
        title: t('adminConsole.tip.delete_error'),
        message: errorMessage,
        color: 'red',
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // 获取用户API密钥
  const getUserApiKeys = async (userUuid: string) => {
    try {
      const keys = await permissionApi.getUserApiKeys(userUuid);
      return keys;
    } catch (error: any) {
      console.error('获取API密钥失败:', error);
      return [];
    }
  };

  // 为用户添加API密钥
  const addUserApiKey = async (userUuid: string, apiKey: string, description?: string) => {
    try {
      const success = await permissionApi.addUserApiKey(userUuid, apiKey, description);
      return success;
    } catch (error: any) {
      return false;
    }
  };

  // 删除用户API密钥
  const deleteUserApiKey = async (userUuid: string, apiKey: string) => {
    try {
      const success = await permissionApi.deleteUserApiKey(userUuid, apiKey);
      if (success) {
        notifications.show({
          message: t('adminConsole.newConsole.member.apiKey.deleteSuccess'),
          color: 'green',
        });
      }
      return success;
    } catch (error: any) {
      notifications.show({
        message: t('adminConsole.newConsole.member.apiKey.deleteFailed'),
        color: 'red',
      });
      return false;
    }
  };

  const batchDeleteUsers = async (userIds: string[]) => {
    try {
      setLoading(true);
      const success = await permissionApi.batchDeleteUsers(userIds);
      // 只更新成员数据，不需要重新加载组织架构
      await loadMembersData(true);
      if (success) {
        notifications.show({
          message: t('adminConsole.tip.delete_success'),
          color: 'green',
        });
      }
      return success;
    } catch (error: any) {
      notifications.show({
        message: t('adminConsole.tip.delete_error'),
        color: 'red',
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    organizationData,
    membersData,
    unassignedUsers,
    roles,
    departments,
    companyName,
    // 方法
    init,
    loadOrganizationData,
    loadOrganizationDataWithCompanyName,
    loadMembersData,
    loadUnassignedUsers,
    loadAssignableRoles,
    createUser,
    updateUser,
    updateUserDepartmentAndRole,
    toggleUserStatus,
    resetPassword,
    assignUserToDepartment,
    batchImportUsers,
    createDepartment,
    updateDepartment,
    deleteDepartment,
    setOrganizationData,
    setMembersData,
    // API密钥相关方法
    getUserApiKeys,
    addUserApiKey,
    deleteUserApiKey,
    batchDeleteUsers,
  };
};
